import { Button } from 'designSystem/components/Button'
import { ChevronDown } from 'lucide-react'
import { type ReactNode, useId, useState } from 'react'
import styled from 'styled-components'

import {
  type ExpandedSection,
  isSubjectOpen,
  toggleDetails,
  toggleSubject,
} from './mobileDetailSectionState'

const Content = styled.div`
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: contain;
`

const Header = styled.button`
  justify-content: space-between;
  width: 100%;
  height: auto;
  min-height: 2.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0;
  border-top: 0.0625rem solid var(--border);
  font-size: 0.875rem;

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }
`

export function MobileDetailSections({
  subject,
  subjectUid,
  subjectLabel,
  details,
  detailsLabel,
  children,
}: {
  subject?: ReactNode
  subjectUid?: string | null
  subjectLabel: string
  details: ReactNode
  detailsLabel: string
  children?: ReactNode
}) {
  const id = useId()
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null)
  const detailsOpen = expandedSection?.type === 'details'
  const subjectOpen = isSubjectOpen(expandedSection, subjectUid)

  return (
    <>
      {subject ? (
        <>
          <Button
            asChild
            type='button'
            variant='ghost'
            aria-expanded={subjectOpen}
            aria-controls={`${id}-subject`}
            onClick={() =>
              setExpandedSection(toggleSubject(expandedSection, subjectUid))
            }>
            <Header>
              {subjectLabel}
              <ChevronDown aria-hidden />
            </Header>
          </Button>
          <Content id={`${id}-subject`} hidden={!subjectOpen}>
            {subject}
          </Content>
        </>
      ) : null}
      <Content>{children}</Content>
      <Button
        asChild
        type='button'
        variant='ghost'
        aria-expanded={detailsOpen}
        aria-controls={`${id}-details`}
        onClick={() => setExpandedSection(toggleDetails(expandedSection))}>
        <Header>
          {detailsLabel}
          <ChevronDown aria-hidden />
        </Header>
      </Button>
      <Content id={`${id}-details`} hidden={!detailsOpen}>
        {details}
      </Content>
    </>
  )
}
